/**
 * fleetDisplayName — the human-readable name of the fleet this instance serves,
 * from the `fleet_slug` in `/api/config`: `orion` → `Orion`. Casing happens in
 * JS because `document.title` is plain text, so the Tailwind `capitalize` class
 * that `AppShell`/`Unauthenticated` put on the raw slug has nothing to hook. CSS
 * `capitalize` treats `-` as a word boundary, so those two render a multi-word
 * slug as `North-East` while this helper gives `North East`: a divergence in the
 * hyphen, latent while every fleet slug is one word.
 *
 * Splitting and casing mirror Go's `fleetprofile.Titleize`: split on `-`,
 * uppercase each segment's first character, keep the rest, so `IRIS` stays
 * `IRIS` and `_` is not a separator. Malformed input diverges on purpose, where
 * Titleize would emit stray separators: whitespace separates too, and empty
 * segments are dropped. A slug that reduces to nothing yields `''` — this names
 * a fleet, and there is no honest name to give — so every caller must guard the
 * empty string rather than write it to a tab title. `?? ''` covers a
 * `fleet_slug` missing from the unvalidated `requestPublic<ClientConfig>` cast,
 * which the type cannot rule out.
 */
export function fleetDisplayName(fleetSlug: string): string {
  return (fleetSlug ?? '')
    .split(/[-\s]+/)
    .filter(part => part !== '')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}
